<template>
	<BaseTabView
		ref="baseTabView"
		:tabs="tabs"
		:active="dActive"
		@active-changed="onActiveTabChange"
		@clicked-tab-index="onTabClick"
		v-slot="{
			pTabs,
			mSelect,
			mFirstTab,
			mNextTab,
			mPrevTab,
			mLastTab
		}"
	>
		<div
			:id="id"
			v-bind="$attrs"
			class="tab-view"
			:class="{ mobile: mobileSelect }"
			role="tablist"
			:aria-label="title"
		>
			<div v-if="showTabs" class="tabs-bar">
				<div class="tabs">
					<div
						v-for="(tab, index) in pTabs"
						:key="tab.id"
						:class="['tab-view-button-wrapper', { active: tab.active }]"
					>
						<TabViewButton
							:id="`${id}-tab-${tab.id}`"
							class="tab-view-button"
							:ariaLabel="tab.ariaLabel"
							:active="tab.active"
							:focused="tab.focused"
							:dataRPATagId="tab.dataRPATagId"
							:controls="`${id}-tab-pane-${tab.id}`"
							:index="index"
							:disabled="tab.disabled"
							:select="mSelect"
							:firstTab="mFirstTab"
							:nextTab="mNextTab"
							:prevTab="mPrevTab"
							:lastTab="mLastTab"
						>
							<slot :name="`tab-inner-${tab.id}`" :tab="tab">
								{{ tab.title }}
							</slot>
						</TabViewButton>
					</div>
				</div>
			</div>
			<div v-if="showSelect" class="tabs-select">
				<TabViewSelect
					ref="tabViewSelect"
					:id="`${id}-tab-view-select`"
					:options="options"
					:label="selectLabel"
					:selected="dActive"
					@selected-change-index="onSelectedChangeIndex"
				/>
			</div>
			<TabViewPanel
				v-for="tab in pTabs"
				:key="tab.id"
				:id="`${id}-tab-pane-${tab.id}`"
				:labelledBy="`${id}-tab-${tab.id}`"
				:active="tab.active"
			>
				<slot :name="`tab-pane-${tab.id}`" :tab="tab" />
			</TabViewPanel>
		</div>
	</BaseTabView>
</template>

<script>
import { BaseTabView } from "../base";
import { TabViewButton, TabViewPanel } from "../base";
import TabViewSelect from "./TabViewSelect";
import { EventBus } from "@vg-faset/core";

/**
 * A re-implementation of Tab View component. This component makes use of some of the more advanced aspects of Vue. If you are not familar with slots in the context of Vue see documentation [here](https://vuejs.org/v2/guide/components-slots.html).
 */
export default {
	name: "TabView",

	inheritAttrs: false,

	mql: null,

	model: {
		prop: "active",
		event: "active-changed"
	},

	components: {
		BaseTabView,
		TabViewButton,
		TabViewPanel,
		TabViewSelect
	},

	data() {
		return {
			dActive: 0,
			isMobile: false
		};
	},

	props: {
		/**
		 * This is the id of the Tab View element, like an HTML id attribute this should be unique across the page.
		 */
		id: {
			type: String,
			default: "",
			required: true
		},

		/**
		 * This is the title of the Tab View. This is used for accessibility purposes.
		 */
		title: {
			type: String,
			// required: true,
			default: ""
		},

		/**
		 * An array of Tab objects which drives the Tab View. The only requirement is that each Tab object contain an id and title property: `[{id: 'some-id', title: 'Some Title'}]`. An optional 'disabled' property is available: `[{id: 'some-id', title: 'Some Title', disabled: true}]`. The default value is false.
		 */
		tabs: {
			type: Array,
			default: () => []
		},

		/**
		 * Change the active Tab by number index.
		 */
		active: {
			type: Number,
			default: 0
		},

		/**
		 * **A11Y** A descriptive label that screen readers will read for the Select list that displays in mobile viewport.
		 */
		selectLabel: {
			type: String,
			// required: true,
			default: ""
		},

		/**
		 * Flag to toggle mobile select. By default on mobile viewports the Tabs are represented as a Select List.
		 */
		mobileSelect: {
			type: Boolean,
			default: true
		}
	},

	computed: {
		options() {
			return this.tabs.map((tab) => {
				return {
					value: tab.title,
					label: tab.title,
					disabled: tab.disabled
				};
			});
		},

		showTabs() {
			return !this.mobileSelect || (this.mobileSelect && !this.isMobile);
		},

		showSelect() {
			return this.isMobile && this.mobileSelect;
		}
	},

	methods: {
		onActiveTabChange(index) {
			this.dActive = index;

			/**
			 * This emits the index value when the active tab changes.
			 *
			 * @action
			 */
			this.$emit("active-changed", index);

			// required for TableFixedLeft, PDP's App.vue, and other V1 components
			// REMOVE FOR V2!
			EventBus.$emit("tab:change");
		},

		onTabClick(index) {
			/**
			 * This emits the tab data when a tab item is clicked.
			 *
			 * @action
			 */
			this.$emit("clicked-tab-data", this.tabs[index]);
		},

		onSelectedChangeIndex(index) {
			this.dActive = index;
			this.$emit("clicked-tab-data", this.tabs[index]);
		},

		onQueryMedia(e) {
			this.isMobile = e.matches;
		},

		focusTab(index) {
			this.$nextTick(() => {
				if (this.showTabs && this.$refs.baseTabView) {
					this.$refs.baseTabView.updateFocused(index);
				} else if (this.showSelect && this.$refs.tabViewSelect) {
					this.$refs.tabViewSelect.focusBaseSelectButton();
				}
			});
		}
	},

	watch: {
		active: {
			immediate: true,
			handler: function (index) {
				this.dActive = index;
			}
		}
	},

	created() {
		this.mql = window.matchMedia("(max-width: 767px)");

		if (this.mql.matches) {
			this.isMobile = true;
		} else {
			this.isMobile = false;
		}

		this.mql.addListener(this.onQueryMedia);
	},

	destroyed() {
		this.mql.removeListener(this.onQueryMedia);
	}
};
</script>

<style lang="scss" scoped>
.tab-view-button {
	color: $color--almost-black;
	display: block;
	font-family: $font--default;
	font-size: 15px;
	letter-spacing: em(-0.15, 15);
	line-height: 15px;
	padding: 18px 0 15px;
	border: 0;
	background-color: $color--white;
	cursor: pointer;

	&.disabled {
		color: $color--light-gray;
		pointer-events: none;
	}

	&.active {
		color: $color--almost-black;
		font-weight: $font-weight--bold;
	}
}

.tabs-bar {
	margin-bottom: 24px;
	position: relative;
}

.tabs {
	display: flex;
	align-items: flex-start;
	border-bottom: 2px solid $color--lighter-gray;
	list-style: none;
	margin: 0;

	.tab-view-button-wrapper {
		margin: 0 30px 0 0;
		padding: 0;

		&.active {
			border-bottom: 6px solid $color--red;
			margin-bottom: -1px;
		}
	}
}

@include from-md {
	.tab-view-button:hover {
		color: $color--gray;
	}
}

@include to-md {
	.tab-view-button {
		color: $color--almost-black;
		font-weight: normal;

		&:hover {
			color: $color--gray;

			&.disabled {
				color: $color--light-gray;
			}
		}
	}
}
</style>
